import React, { useState } from 'react';

import { Controller } from 'react-hook-form';

import AdminFilter from 'components/admin/AdminFilter/view';
import { defaultMapFiltersToRequest } from 'components/admin/AdminTableListPage/view';
import Button from 'components/common/ui-kit/Button';
import { InputV2 } from 'components/common/ui-kit/Input/view';
import ReactSelect from 'components/common/ui-kit/Select/view';
import useAppDispatch from 'hooks/common/useAppDispatch';
import useAppSelector from 'hooks/common/useAppSelector';
import { useInitPageFilters } from 'hooks/moderator/useInitFilters';
import { SelectOption } from 'models/common';
import { showModal } from 'redux/actions';
import { userManagementApi } from 'redux/api/staff/admin/user-management-api';
import { createInputV2Props, createSelectProps } from 'utils/form-helpers';

import AdminUserListElement from '../../../components/admin/AdminUserListElement';
import Pagination from '../../../components/common/ui-kit/Pagination';
import { UserRole } from '../../../models/user';

enum Status {
  ACTIVE,
  ARCHIVED,
}

const statusOptions: SelectOption[] = [
  { label: 'Активный', value: Status.ACTIVE },
  { label: 'Удалён', value: Status.ARCHIVED },
];

const mapRoleToString = new Map<UserRole, string>([
  [1, 'ADMIN'],
  [2, 'COORDINATOR'],
  [3, 'MODERATOR'],
  [4, 'INSTITUTE_STAFF'],
  [5, 'SUPPORT'],
  [6, 'SPECIALIST'],
  [7, 'EXPERT'],
  [8, 'CALL_CENTER'],
  [9, 'USER'],
]);

const roleOptions: SelectOption[] = [
  { label: 'Администратор', value: UserRole.ADMIN },
  { label: 'Координатор', value: UserRole.COORDINATOR },
  { label: 'Модератор', value: UserRole.MODERATOR },
  { label: 'Сотрудник института', value: UserRole.INSTITUTE_STAFF },
  { label: 'Поддержка', value: UserRole.SUPPORT },
  { label: 'Специалист', value: UserRole.SPECIALIST },
  { label: 'Эксперт', value: UserRole.EXPERT },
  { label: 'Сотрудник Колл-центра', value: UserRole.CALL_CENTER },
  { label: 'Пользователь', value: UserRole.USER },
];

export const defaultUsersFilters = {
  id: '',
  lastName: '',
  firstName: '',
  middleName: '',
  email: '',
  active: null,
};

export const mapUserFiltersToRequest = (filters, map) => {
  const res = map(filters);
  const status = filters.status;
  if (status !== undefined) {
    res.active = filters.status === Status.ACTIVE;
    delete res.status;
  }
  res.role = mapRoleToString.get(filters.role);
  return res;
};

export const renderUserAdministrationFilters = ({
  control,
  formState: { errors },
  visibleFilters,
}) => {
  const inputV2Props = createInputV2Props(errors);
  const selectProps = createSelectProps(errors);
  return (
    <>
      {visibleFilters.includes('id') && (
        <Controller
          name="id"
          control={control}
          render={({ field }) => (
            <InputV2
              className="filter-form__field"
              placeholder="ID пользователя"
              {...inputV2Props(field)}
            />
          )}
        />
      )}
      {visibleFilters.includes('lastName') && (
        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <InputV2
              className="filter-form__field"
              placeholder="Фамилия"
              {...inputV2Props(field)}
            />
          )}
        />
      )}
      {visibleFilters.includes('firstName') && (
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <InputV2
              className="filter-form__field"
              placeholder="Имя"
              {...inputV2Props(field)}
            />
          )}
        />
      )}
      {visibleFilters.includes('middleName') && (
        <Controller
          name="middleName"
          control={control}
          render={({ field }) => (
            <InputV2
              className="filter-form__field"
              placeholder="Отчество"
              {...inputV2Props(field)}
            />
          )}
        />
      )}
      {visibleFilters.includes('email') && (
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <InputV2
              className="filter-form__field"
              placeholder="Email"
              {...inputV2Props(field)}
            />
          )}
        />
      )}
      {visibleFilters.includes('active') && (
        <Controller
          name="active"
          control={control}
          render={({ field }) => (
            <ReactSelect<SelectOption>
              options={statusOptions}
              placeholder="Статус"
              className="filter-form__field"
              isClearable
              {...selectProps(field, {
                options: statusOptions,
              })}
            />
          )}
        />
      )}
      {visibleFilters.includes('role') && (
        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <ReactSelect<SelectOption>
              options={roleOptions}
              placeholder="Роль"
              className="filter-form__field"
              {...selectProps(field, {
                options: roleOptions,
              })}
            />
          )}
        />
      )}
    </>
  );
};

const UsersPage = () => {
  const dispatch = useAppDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  useInitPageFilters('users');
  const visibleFilters =
    useAppSelector(state => state.visibleFilters.users) ?? [];

  const [filters, setFilters] = useState({});

  const onFiltersReset = () => {
    setCurrentPage(1);
    setFilters({});
  };

  const onFiltersSubmit = filters => {
    setCurrentPage(1);
    setFilters(filters);
  };

  const { data } = userManagementApi.useGetAllUsersByAdminQuery({
    request: mapUserFiltersToRequest(filters, defaultMapFiltersToRequest),
    page: currentPage,
    size: perPage,
  });

  const handleCreateUserClick = () => {
    dispatch(
      showModal({
        name: 'ADMIN_USER',
        data: {},
      })
    );
  };

  return (
    <main className="moderator-contract-page">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '64px',
          marginBottom: '24px',
        }}
      >
        <h1
          style={{
            lineHeight: '32px',
            fontSize: '24px',
          }}
        >
          Все пользователи
        </h1>
        <Button type="button" theme="success" onClick={handleCreateUserClick}>
          Добавить пользователя
        </Button>
      </div>
      {/* <AccordionTab title="Фильтры">
        <AdminUserListFilter />
      </AccordionTab> */}
      <AdminFilter
        defaultValues={defaultUsersFilters}
        filter={filters}
        onSubmit={onFiltersSubmit}
        onReset={onFiltersReset}
        displayCount={data?.totalElements}
        displayCountLabel="Пользователей"
        filterSettingsPageKey="users"
      >
        {form => renderUserAdministrationFilters({ ...form, visibleFilters })}
      </AdminFilter>
      {data?.result.map((item, index) => (
        <AdminUserListElement user={item} key={index} />
      ))}
      <Pagination
        lastPage={data?.totalPages ?? 0}
        currentPage={currentPage}
        perPage={perPage}
        onPageChange={value => setCurrentPage(value)}
        onPerPageChange={value => setPerPage(value)}
      />
    </main>
  );
};

export default UsersPage;
